import { Capacitor } from '@capacitor/core';
import { AppsFlyer } from 'appsflyer-capacitor-plugin';
import type { AFEvent as AFEventType } from 'appsflyer-capacitor-plugin';
import { APPSFLYER_CONFIG } from '@/config/appsflyer';

let isInitialized = false;

/**
 * Initialize the AppsFlyer SDK (call once at app startup on native platforms)
 */
export async function initAppsFlyer(): Promise<void> {
  if (!Capacitor.isNativePlatform() || isInitialized) return;

  try {
    const result = await AppsFlyer.initSDK({
      devKey: APPSFLYER_CONFIG.devKey,
      appID: APPSFLYER_CONFIG.appId,
      isDebug: APPSFLYER_CONFIG.isDebug,
      waitForATTUserAuthorization: APPSFLYER_CONFIG.waitForATTUserAuthorization,
      registerOnDeepLink: true,
    });

    isInitialized = true;
    console.log('AppsFlyer SDK initialized successfully:', result);
  } catch (error) {
    console.error('AppsFlyer SDK initialization failed:', error);
  }
}

// ─── Event name constants ───────────────────────────────────────────────────

export const AF_EVENTS = {
  INSTALL: 'install',
  SIGN_UP: 'af_complete_registration',
  SESSION: 'session',
  FEATURE_USED: 'feature_used',
  APP_DELETED: 'app_deleted',
} as const;

/**
 * Log a custom in-app event to AppsFlyer.
 * Silently no-ops on web or if the SDK hasn't been initialised yet.
 */
export async function logAFEvent(
  eventName: string,
  eventValue: Record<string, unknown> = {},
): Promise<void> {
  if (!Capacitor.isNativePlatform() || !isInitialized) return;

  try {
    await AppsFlyer.logEvent({
      eventName,
      eventValue,
    });
    console.log(`AppsFlyer event logged: ${eventName}`, eventValue);
  } catch (error) {
    console.error(`AppsFlyer event failed (${eventName}):`, error);
  }
}

// ─── Convenience helpers ────────────────────────────────────────────────────

/** Track a successful sign-up */
export const trackSignUp = (method = 'email') =>
  logAFEvent(AF_EVENTS.SIGN_UP, { af_registration_method: method });

/** Track a session start */
export const trackSession = () => logAFEvent(AF_EVENTS.SESSION);

/** Track a feature being used */
export const trackFeatureUsed = (featureName: string) =>
  logAFEvent(AF_EVENTS.FEATURE_USED, { feature_name: featureName });
